/**
 * Platform Users (saas-auth login) role/status badge color pairs (DNKADM-0269) —
 * moved here from `src/features/platform-members/platformBadges.ts` so the pairs
 * are mode-aware and the feature dir stays literal-free (the DNKADM-0269 ESLint
 * guard bans raw colors in `src/features/**`; `src/theme/**` owns them).
 *
 * LIGHT values are byte-identical to the pre-0269 literals (the 0266 identity
 * constraint). Dark values reuse the same families as the equivalent-meaning
 * pairs elsewhere in the token system (`STATUS_DARK`/`TIER_CHIP_DARK` in
 * `../tokens.ts`) so a role/status that already has a semantic twin (Owner ~
 * success/brand, Admin ~ the teal tier, Active/Invited/Disabled ~ the matching
 * `STATUS` entries) renders consistently; `SUPER_ADMIN`'s violet has no existing
 * twin, so it gets its own pastel on the same translucent-tint ramp. `STAFF`
 * (DNKADM-0360 — the new floor role) takes the blue family the avatar ramp already
 * carries (`AVATARS[5]`, mirrored by `members.guestAvatar`): clearly distinct from
 * Owner's green, Admin's teal and User/Guest's neutral, and audited in both modes.
 *
 * The precedence/derivation LOGIC stays in `platformBadges.ts` — this module is
 * data only.
 */
import { GREEN_TINTS, buildSemanticMaps } from '../tokens';

/** One role/status badge pair, with an optional tooltip hint. */
interface PlatformBadgePair {
  label: string;
  fg: string;
  bg: string;
  hint?: string;
}

export interface PlatformMemberBadgeMaps {
  role: Record<
    'OWNER' | 'SUPER_ADMIN' | 'ADMIN' | 'STAFF' | 'USER' | 'GUEST',
    PlatformBadgePair
  >;
  status: Record<'ACTIVE' | 'INVITED' | 'DISABLED', PlatformBadgePair>;
  /** Fallback for a role/status outside the known enum (renders the raw wire value). */
  neutral: { fg: string; bg: string };
}

const NEUTRAL_LIGHT = { fg: '#5B5470', bg: '#ECEAF3' } as const;
const NEUTRAL_DARK = { fg: '#B3ABCB', bg: 'rgba(139,130,168,0.2)' } as const;

const LIGHT: PlatformMemberBadgeMaps = {
  role: {
    OWNER: { label: 'Owner', fg: '#1F6B43', bg: GREEN_TINTS.chip },
    SUPER_ADMIN: {
      label: 'Super admin',
      fg: '#5A3A8A',
      bg: '#EAE2F4',
      hint: 'Elevated administrator',
    },
    ADMIN: { label: 'Admin', fg: '#256A6E', bg: '#E2EDEF' },
    STAFF: { label: 'Staff', fg: '#33567E', bg: '#E7EEF6' },
    USER: { label: 'User', ...NEUTRAL_LIGHT },
    GUEST: { label: 'Guest', ...NEUTRAL_LIGHT },
  },
  status: {
    ACTIVE: { label: 'Active', fg: '#1F6B43', bg: '#E5F1E9' },
    INVITED: {
      label: 'Invited',
      fg: '#8A5A12',
      bg: '#FBEBD3',
      hint: 'Pending — hasn’t joined yet',
    },
    DISABLED: {
      label: 'Disabled',
      fg: '#9A4128',
      bg: '#F4E3DE',
      hint: 'Revoked — this login can no longer sign in',
    },
  },
  neutral: NEUTRAL_LIGHT,
};

const DARK: PlatformMemberBadgeMaps = {
  role: {
    OWNER: { label: 'Owner', fg: '#7FD3A3', bg: buildSemanticMaps('dark').greenTints.chip },
    SUPER_ADMIN: {
      label: 'Super admin',
      fg: '#C9BCE8',
      bg: 'rgba(90,58,138,0.28)',
      hint: 'Elevated administrator',
    },
    ADMIN: { label: 'Admin', fg: '#8FD0D4', bg: 'rgba(37,106,110,0.26)' },
    STAFF: { label: 'Staff', fg: '#9FC0E8', bg: 'rgba(51,86,126,0.32)' },
    USER: { label: 'User', ...NEUTRAL_DARK },
    GUEST: { label: 'Guest', ...NEUTRAL_DARK },
  },
  status: {
    ACTIVE: { label: 'Active', fg: '#7FD3A3', bg: 'rgba(46,157,94,0.18)' },
    INVITED: {
      label: 'Invited',
      fg: '#E8C27A',
      bg: 'rgba(216,154,46,0.16)',
      hint: 'Pending — hasn’t joined yet',
    },
    DISABLED: {
      label: 'Disabled',
      fg: '#F0A090',
      bg: 'rgba(192,73,46,0.18)',
      hint: 'Revoked — this login can no longer sign in',
    },
  },
  neutral: NEUTRAL_DARK,
};

/** Per-mode platform-member role/status badge pairs. Light === the pre-0269 literals. */
export function buildPlatformMemberBadgeMaps(mode: 'light' | 'dark'): PlatformMemberBadgeMaps {
  return mode === 'dark' ? DARK : LIGHT;
}
